import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ThomLogo from "@/components/ThomLogo";

const roleHome = (role) => role === "admin" ? "/admin" : role === "inspector" ? "/inspector" : (role === "seller" || role === "dealer") ? "/seller" : "/buyer";

export const Login = () => {
  const { loginLocal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginLocal(email, password);
      toast.success(`Welcome, ${u.name}`);
      navigate(location.state?.from?.pathname || roleHome(u.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="mb-8 flex justify-center">
          <ThomLogo size={72} stacked showTagline data-testid="login-brand" />
        </div>
        <form onSubmit={submit} className="border border-border p-6 bg-card space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="login-submit-btn">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-center text-xs text-muted-foreground">— or —</div>
          <button type="button" onClick={google} className="btn-outline w-full" data-testid="login-google-btn">
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            Continue with Google
          </button>
          <p className="text-xs text-muted-foreground text-center">
            No account? <Link to="/register" className="text-primary hover:underline">Create one</Link>
          </p>
        </form>
        <div className="mt-6 text-xs text-muted-foreground border-t border-border pt-4">
          <div className="mono">Demo accounts — password &lt;role&gt;@2026</div>
          <ul className="mt-2 space-y-1">
            <li>admin@thomverify.co.zw / Admin@2026</li>
            <li>inspector@thomverify.co.zw / Inspector@2026</li>
            <li>seller@thomverify.co.zw / Seller@2026</li>
            <li>buyer@thomverify.co.zw / Buyer@2026</li>
            <li>dealer@thomverify.co.zw / Dealer@2026</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export const Register = () => {
  const { registerLocal } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await registerLocal(form);
      toast.success(`Welcome, ${u.name}`);
      navigate(roleHome(u.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="mb-6">
          <div className="kicker">Create account</div>
          <h1 className="display text-4xl mt-1">Join THOM Verify™.</h1>
        </div>
        <form onSubmit={submit} className="border border-border p-6 bg-card space-y-4">
          <div>
            <Label>Full name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input" />
          </div>
          <div>
            <Label>I want to</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[
                { v: "buyer", l: "Buy" },
                { v: "seller", l: "Sell" },
                { v: "dealer", l: "Dealer" },
              ].map((r) => (
                <button
                  key={r.v}
                  type="button"
                  onClick={() => setForm({ ...form, role: r.v })}
                  className={`border px-3 py-2 text-sm rounded-sm ${form.role === r.v ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}
                  data-testid={`register-role-${r.v}`}
                >
                  {r.l}
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="register-submit-btn">
            {busy ? "Creating…" : "Create account"}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;
